import { useMemo } from "react";

import type { AllowedImageMime, FileStatus } from "../types/file";
import { getFileMimeType } from "../helpers/getFileMimeType";

import { useBlobUrl } from "./useBlobUrl";
import { useHeicConversion } from "./useHeicConversion";

type UseValidateFileProps = {
  acceptedTypes: readonly AllowedImageMime[];
  file: Nullable<File>;
  maxSizeBytes?: number;
};

export function useValidateFile({ acceptedTypes, file, maxSizeBytes }: UseValidateFileProps) {
  const {
    convertedFile,
    error: conversionError,
    isConverting,
  } = useHeicConversion({ file, quality: 0.85 }); // skips conversion if !heic

  const workFile = convertedFile || file;
  const mimeType = workFile ? getFileMimeType(workFile) : null;
  const fileName = workFile?.name ? workFile.name.replace(/ /g, "_") : null; // replace spaces

  const status: FileStatus = useMemo(() => {
    if (!workFile) return null;
    if (conversionError) return "invalid-type";
    if (isConverting) return "processing";
    if (maxSizeBytes && workFile.size > maxSizeBytes) return "too-large";
    // Validate file type. Warning: heic files do not report file.type on Windows/Linux!
    if (!acceptedTypes.includes(workFile.type as AllowedImageMime)) {
      return "invalid-type";
    }

    return "valid";
  }, [acceptedTypes, conversionError, isConverting, maxSizeBytes, workFile]);

  const fileToPreview = status === "valid" || status === "processing" ? workFile : null;
  const previewUrl = useBlobUrl(fileToPreview);

  return {
    fileName,
    isValid: status === "valid",
    mimeType,
    previewUrl,
    status,
    validatedFile: workFile,
  };
}
